import { uiText, useUiLanguage } from '@/i18n/interface';
import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { getCurrencySymbol } from "@/utils/currencyUtils";

interface PaymentCategory {
  id: string;
  name: string;
  amounts: {
    oneYear: number;
    sixMonths: number;
    threeMonths: number;
    monthly: number;
  };
  currency?: string;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  payment_status?: string;
}

interface PaymentInstructionsTabProps {
  students: Student[];
  categories: PaymentCategory[];
  selectedStudent: string;
  setSelectedStudent: (studentId: string) => void;
  paymentDetails: {
    category: string;
    subscriptionType: "1year" | "6months" | "3months" | "monthly";
    paymentPlan: "1" | "2" | "3";
  };
  setPaymentDetails: (details: any) => void;
  isLoading: boolean;
  onSendPaymentInstructions: () => void;
}

const PaymentInstructionsTab: React.FC<PaymentInstructionsTabProps> = ({
  students,
  categories,
  selectedStudent,
  setSelectedStudent,
  paymentDetails,
  setPaymentDetails,
  isLoading,
  onSendPaymentInstructions,
}) => {
  useUiLanguage();
  const selectedCategory = categories.find(
    (c) => c.name === paymentDetails.category,
  );
  const defaultCurrency = selectedCategory?.currency || "USD"; // Use category currency or default

  // Always use admin's default currency setting
  const displayCurrency = defaultCurrency;
  const currencySymbol = getCurrencySymbol(displayCurrency);

  const totalAmount = selectedCategory
    ? paymentDetails.subscriptionType === "1year"
      ? selectedCategory.amounts.oneYear
      : paymentDetails.subscriptionType === "6months"
        ? selectedCategory.amounts.sixMonths
        : paymentDetails.subscriptionType === "3months"
          ? selectedCategory.amounts.threeMonths
          : paymentDetails.subscriptionType === "monthly"
            ? selectedCategory.amounts.monthly
            : selectedCategory.amounts.oneYear
    : 0;

  // Format amount for display (amounts are already in decimal format)
  const formattedTotalAmount = totalAmount.toFixed(2);
  const installmentAmount =
    totalAmount === 0 ? 0 : totalAmount / parseInt(paymentDetails.paymentPlan);
  const formattedInstallmentAmount = installmentAmount.toFixed(2);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{uiText("Send Payment Instructions")}</CardTitle>
        <CardDescription>
          {uiText("Send payment instructions to students with their specific amounts and plans. Using admin default currency:")}{' '}{defaultCurrency}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="student-select">{uiText("Select Student")}</Label>
            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
              <SelectTrigger id="student-select">
                <SelectValue placeholder={uiText("Choose a student")} />
              </SelectTrigger>
              <SelectContent>
                {students.map((student) => (
                  <SelectItem key={student.id} value={student.id}>
                    {student.first_name} {student.last_name} ({student.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="category-select">{uiText("Payment Category")}</Label>
            <Select
              value={paymentDetails.category}
              onValueChange={(value) =>
                setPaymentDetails({ ...paymentDetails, category: value })
              }
            >
              <SelectTrigger id="category-select">
                <SelectValue placeholder={uiText("Choose category")} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.name}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subscription-select">{uiText("Subscription Type")}</Label>
            <Select
              value={paymentDetails.subscriptionType}
              onValueChange={(value) =>
                setPaymentDetails({
                  ...paymentDetails,
                  subscriptionType: value as
                    | "1year"
                    | "6months"
                    | "3months"
                    | "monthly",
                })
              }
            >
              <SelectTrigger id="subscription-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1year">{uiText("1 Year")}</SelectItem>
                <SelectItem value="6months">{uiText("6 Months")}</SelectItem>
                <SelectItem value="3months">{uiText("3 Months")}</SelectItem>
                <SelectItem value="monthly">{uiText("Monthly")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment-plan-select">{uiText("Payment Plan")}</Label>
            <Select
              value={paymentDetails.paymentPlan}
              onValueChange={(value) =>
                setPaymentDetails({
                  ...paymentDetails,
                  paymentPlan: value as "1" | "2" | "3",
                })
              }
            >
              <SelectTrigger id="payment-plan-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">{uiText("Full Payment")}</SelectItem>
                <SelectItem value="2">{uiText("2 Installments")}</SelectItem>
                <SelectItem value="3">{uiText("3 Installments")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {selectedCategory && (
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h4 className="font-medium mb-2">{uiText("Payment Summary")}</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {uiText("Total Amount:")}{' '}{currencySymbol}
              {formattedTotalAmount}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {uiText("Installments:")}{' '}{paymentDetails.paymentPlan}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {uiText("Amount per installment:")}{' '}{currencySymbol}
              {formattedInstallmentAmount}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {uiText("Currency:")}{' '}{defaultCurrency} {' '}{uiText("(Admin Default)")}</p>
          </div>
        )}

        <Button
          onClick={onSendPaymentInstructions}
          disabled={!selectedStudent || !paymentDetails.category || isLoading}
          className="w-full"
        >
          {isLoading ? uiText("Sending...") : uiText("Send Payment Instructions")}
        </Button>
      </CardContent>
    </Card>
  );
};

export default PaymentInstructionsTab;
